/**
 * A damped spring on one axis, integrated per animation frame — the whole of
 * the rail's drag physics, and deliberately not a dependency.
 *
 * Why physics at all: the dragged tile is rendered as a ghost that FOLLOWS the
 * pointer through a spring rather than being pinned to it; its rotation is
 * taken from its horizontal velocity, so it leans the way a card held by one
 * corner does; and on release it springs to where it is going — into the
 * target, or back home — instead of teleporting. None of that is possible
 * with HTML5 drag-and-drop, which owns the drag image.
 *
 * Both springs are tuned just UNDER critical damping (ζ ≈ 0.99): the tile
 * tracks the pointer the way a browser tab does — immediately, with a hair of
 * weight — and lands without a bounce. They were noticeably looser (ζ ≈ 0.68)
 * until 2026-08-30, which trailed the pointer far enough to read as lag and
 * wobbled on landing; the lean is what carries the sense of weight now, and
 * it is half what it was for the same reason.
 *
 * Semi-implicit Euler, SUB-STEPPED: velocity is updated first and the position
 * uses the NEW velocity, and the frame's elapsed time is integrated in slices
 * of at most `MAX_STEP_S` rather than in one go.
 *
 * The sub-stepping is what lets the springs be this stiff. Explicit
 * integration of a damped spring diverges once `damping · h > 2` — at one
 * step per frame that ties the damping (and so the stiffness, and so how
 * closely the ghost can track the pointer) to whatever `dt` the browser hands
 * over. A tuning that felt right at 60fps then exploded on a resumed
 * background tab. Slicing the step decouples the two: the feel is a property
 * of the springs, not of the frame rate, and a 30fps machine gets the same
 * gesture as a 120Hz one.
 *
 * `MAX_CATCHUP_S` is the other half of that guard: it bounds how much REAL
 * time one call will integrate, so a tab that was hidden for a minute resumes
 * with the ghost near the pointer instead of spending a minute of frames
 * catching up.
 */

export interface SpringConfig {
  /** Restoring force per unit of displacement. Higher = snappier. */
  stiffness: number
  /** Velocity damping. Around 2·√stiffness is critical (no overshoot). */
  damping: number
}

export interface SpringState {
  value: number
  velocity: number
}

/** Follows the pointer: stiff and all but critically damped, so the ghost is
 *  under the cursor rather than trailing it, with only the last few pixels of
 *  give that keep it from feeling glued on. */
export const RAIL_FOLLOW_SPRING: SpringConfig = { stiffness: 6400, damping: 160 }

/** Springs home or into the target on release: softer than the follow (the
 *  travel is longer), still critically damped, so it lands and stops. */
export const RAIL_SETTLE_SPRING: SpringConfig = { stiffness: 900, damping: 60 }

/** The integrator's own step, in seconds. Every spring here must satisfy
 *  `damping · MAX_STEP_S < 2` and `stiffness · MAX_STEP_S² < 4` to stay
 *  stable — at 1/120 that is damping < 240 and stiffness < 57600, which is
 *  well clear of both rail springs. */
export const MAX_STEP_S = 1 / 120

/** The most REAL time a single call will integrate, however long the caller
 *  was away. */
export const MAX_CATCHUP_S = 1 / 15

export function stepSpring(s: SpringState, target: number, cfg: SpringConfig, dtSeconds: number): SpringState {
  let remaining = Math.min(Math.max(dtSeconds, 0), MAX_CATCHUP_S)
  let state = s
  while (remaining > 0) {
    const h = Math.min(remaining, MAX_STEP_S)
    const accel = -cfg.stiffness * (state.value - target) - cfg.damping * state.velocity
    const velocity = state.velocity + accel * h
    state = { value: state.value + velocity * h, velocity }
    remaining -= h
  }
  return state
}

/** True once the spring is close enough to the target, and slow enough, that
 *  another frame would not be visible. */
export function isSettled(s: SpringState, target: number, epsilon = 0.1, velocityEpsilon = 1): boolean {
  return Math.abs(s.value - target) < epsilon && Math.abs(s.velocity) < velocityEpsilon
}

/** The swing: how far the ghost tilts for a given horizontal velocity, in
 *  degrees. Linear until the clamp, so a slow drag barely tilts and a fast
 *  flick leans; odd-symmetric, so leftward and rightward mirror. Deliberately
 *  restrained — at 22° over 1400px/s it tilted on an ordinary reorder, which
 *  is not what dragging a tab looks like. */
export function swingAngle(velocityXPxPerSecond: number, maxDegrees = 12, velocityAtMax = 1600): number {
  const t = velocityXPxPerSecond / velocityAtMax
  return Math.max(-1, Math.min(1, t)) * maxDegrees
}

/** Honour the OS "reduce motion" setting: with it on, the ghost snaps to the
 *  pointer and to its destination instead of springing. */
export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** How long a tile that is MAKING ROOM takes to get there. Also how long the
 *  released pill glides into its slot — the two must agree, or the list
 *  re-renders under an element that is still moving. */
export const RAIL_SHIFT_MS = 380

/** How a tile that is MAKING ROOM for the dragged one moves. One constant for
 *  the rail, a folder's member list, the workspace tab strip and the agent's
 *  session tabs: the same gesture reading at two speeds on two surfaces is
 *  what makes a drag feel improvised.
 *
 *  Unhurried, and ease-OUT with a long tail: it leaves at once and takes its
 *  time settling. The dragged item is the one under the pointer and must be
 *  exact; the tiles getting out of its way are scenery, and scenery that snaps
 *  reads as a jolt. 180ms, then 260ms, both still read as a snap — this is the
 *  curve macOS uses for the same job, where nearly all the distance is covered
 *  early and the last few pixels drift in. */
export const RAIL_SHIFT_TRANSITION = `transform ${RAIL_SHIFT_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`
